/**
 * Advanced View Page - Unified Hub for Advanced Features
 * 
 * Sections:
 * - Assets & Inventory
 * - Currency & FX
 * - Data Management
 * - Accounting Tools
 * - Period Management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { useBilling } from '../contexts/BillingContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { getCurrencySymbol } from '../utils/currency';
import { isIOSNative } from '../utils/nativeBridge';
import { isPayrollCountry } from '../components/payroll/PayrollCountryGate';
import { mileageCopy } from '../lib/mileageCountry';
import { Car,
  Boxes,
  Briefcase,
  DollarSign,
  Upload,
  Download,
  BookOpen,
  Edit3,
  RefreshCw,
  FileSpreadsheet,
  Calendar,
  Loader2,
  ArrowRight,
  ChevronRight,
  Package,
  Calculator,
  TrendingUp,
  Settings,
  Lock,
  Eye,
  Users,
  Brain,
  History
} from 'lucide-react';

const AdvancedViewPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { business } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'accounting');
  
  // Quick stats
  const [stats, setStats] = useState({
    accounts: 0,
    manualJournals: 0,
    recurringJournals: 0,
    pendingPeriods: 0,
    pendingSettlements: { count: 0, value: 0, inflows: 0, outflows: 0 }
  });

  const currencySymbol = getCurrencySymbol(business?.currency || 'NGN');

  useEffect(() => {
    fetchQuickStats();
  }, []);

  const fetchQuickStats = async () => {
    setLoading(true);
    try {
      const [coaRes, journalsRes, recurringRes, settlementRes] = await Promise.all([
        axios.get(`${API}/accounting/chart-of-accounts`).catch(() => ({ data: [] })),
        axios.get(`${API}/accounting/journals?source=manual&limit=100`).catch(() => ({ data: [] })),
        axios.get(`${API}/accounting/recurring-journals`).catch(() => ({ data: { recurring_journals: [] } })),
        axios.get(`${API}/cash-settlement/pending`).catch(() => ({ data: { summary: {} } }))
      ]);

      const settlementSummary = settlementRes.data?.summary || {};

      setStats({
        accounts: coaRes.data?.length || 0,
        manualJournals: journalsRes.data?.length || 0,
        recurringJournals: recurringRes.data.recurring_journals?.length || 0,
        pendingPeriods: 0,
        pendingSettlements: {
          count: (settlementRes.data?.items?.expected_inflows?.length || 0) + (settlementRes.data?.items?.expected_outflows?.length || 0),
          value: Math.abs(settlementSummary.net_cash_flow_expected || 0),
          inflows: settlementSummary.total_expected_inflows || 0,
          outflows: settlementSummary.total_expected_outflows || 0
        }
      });
    } catch (err) {
      console.log('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${currencySymbol}${Number(amount || 0).toLocaleString()}`;
  };

  // Section definitions with their features.
  // Sprint 3 F3.1 - unified labels + surface orphan pages (Period Close,
  // Tax Centre, Reconciliation, AI Classifier Quality, Scheduled Reports,
  // Settlement Dashboard, Migration & Imports).
  const sections = {
    accounting: {
      label: 'Books',
      icon: BookOpen,
      description: 'Categories, adjustments, and the underlying ledger',
      features: [
        {
          id: 'coa',
          title: 'Categories (Chart of Accounts)',
          description: 'The buckets we use to organise your money',
          icon: BookOpen,
          color: 'blue',
          path: '/chart-of-accounts',
          stat: `${stats.accounts} accounts`
        },
        {
          id: 'manual-journals',
          title: 'Adjustments (Manual Journals)',
          description: 'Create adjusting entries and corrections',
          icon: Edit3,
          color: 'purple',
          path: '/manual-journals',
          stat: `${stats.manualJournals} entries`
        },
        {
          id: 'recurring-journals',
          title: 'Repeating Entries (Recurring Journals)',
          description: 'Automated journal entries (rent, depreciation)',
          icon: RefreshCw,
          color: 'teal',
          path: '/recurring-journals',
          stat: `${stats.recurringJournals} active`
        },
        {
          id: 'system-journals',
          title: 'Auto Entries (System Journals)',
          description: 'View auto-generated entries (read-only)',
          icon: Lock,
          color: 'slate',
          path: '/system-journals'
        },
        {
          id: 'general-ledger',
          title: 'All Transactions (General Ledger)',
          description: 'Drill down into account transactions',
          icon: FileSpreadsheet,
          color: 'indigo',
          path: '/general-ledger'
        },
        {
          id: 'reconciliation',
          title: 'Match Transactions (Reconciliation)',
          description: 'Tie your books out to the bank statement',
          icon: RefreshCw,
          color: 'cyan',
          path: '/bank-feeds'
        },
        ...(isPayrollCountry(business?.country) ? [{
          id: 'payroll',
          title: 'Payroll',
          description: 'Manage employee salaries and deductions',
          icon: Users,
          color: 'emerald',
          path: '/payroll',
          feature: 'payroll',      // iter1106 - gated to Growth+
        }] : [])
      ]
    },
    assets: {
      label: 'Operations',
      icon: Briefcase,
      description: 'Equipment, inventory, prepayments and deferrals',
      features: [
        {
          id: 'fixed-assets',
          title: 'Equipment & Property (Fixed Assets)',
          description: 'AI-powered asset categorisation & depreciation',
          icon: Briefcase,
          color: 'emerald',
          path: '/fixed-assets',
          badge: 'AI'
        },
        {
          id: 'inventory',
          title: 'Inventory',
          description: 'Stock management and tracking',
          icon: Boxes,
          color: 'orange',
          path: '/inventory'
        },
        {
          id: 'prepaid-expenses',
          title: 'Paid in Advance (Prepaid Expenses)',
          description: 'AI-powered prepayment schedules and amortisation',
          icon: Calendar,
          color: 'purple',
          path: '/prepaid-expenses',
          badge: 'AI'
        },
        {
          id: 'deferred-revenue',
          title: 'Prepaid by Customers (Deferred Revenue)',
          description: 'Revenue recognition schedules',
          icon: Calendar,
          color: 'teal',
          path: '/deferred-revenue'
        }
      ]
    },
    currency: {
      label: 'Currency & FX',
      icon: DollarSign,
      description: 'Exchange rates and multi-currency tools',
      features: [
        {
          id: 'fx-rates',
          title: 'Currency Rates',
          description: 'View and manage exchange rates',
          icon: DollarSign,
          color: 'green',
          path: '/fx-rates'
        },
        {
          id: 'fx-gain-loss',
          title: 'FX Gain / Loss',
          description: 'Unrealised and realised currency gains',
          icon: TrendingUp,
          color: 'cyan',
          path: '/reports?report=fx_gainloss'
        }
      ]
    },
    periods: {
      label: 'Period & Tax',
      icon: Calendar,
      description: 'Closing months, tax filings, year-end',
      features: [
        {
          id: 'period-close',
          title: 'Lock the Month (Period Close)',
          description: 'Close monthly periods so they stop changing. Year-End close lives here too.',
          icon: Lock,
          color: 'rose',
          path: '/period-close'
        },
        {
          id: 'annual-tax',
          title: 'Annual Tax Computation',
          description: 'Sector-aware corporate income tax (CIT/PPT) and special-sector levies',
          icon: Calculator,
          color: 'fuchsia',
          path: '/annual-tax-computation'
        },
        {
          id: 'mileage',
          title: `Mileage · ${mileageCopy(business?.country).authority}`,
          description: mileageCopy(business?.country).more_tools_description,
          icon: Car,
          color: 'sky',
          path: '/mileage'
        },
        {
          id: 'tax-compliance',
          title: 'Tax & Compliance Setup',
          description: 'Configure tax types, rates, filing frequencies and governance',
          icon: FileSpreadsheet,
          color: 'amber',
          path: '/tax-compliance'
        },
        {
          id: 'tax-remittance',
          title: 'Tax Remittance',
          description: 'Record tax payments to FIRS / HMRC',
          icon: Upload,
          color: 'violet',
          path: '/tax-remittance'
        }
      ]
    },
    data: {
      label: 'Data & Imports',
      icon: Upload,
      description: 'Import historical records and export your data',
      features: [
        {
          id: 'import',
          title: 'Migration & Imports',
          description: 'Bring in opening balances and historical data',
          icon: Upload,
          color: 'violet',
          path: '/data-migration'
        },
        {
          id: 'export',
          title: 'Export Data',
          description: 'Download invoices, ledger, trial balance & more',
          icon: Download,
          color: 'sky',
          path: '/data-export'
        },
        {
          id: 'scheduled-reports',
          title: 'Scheduled Reports',
          description: 'Auto-email reports on a schedule',
          icon: Calendar,
          color: 'blue',
          path: '/scheduled-reports'
        },
        {
          id: 'opening-balances',
          title: 'Starting Balances (Opening Balances)',
          description: 'Set the position you started with',
          icon: FileSpreadsheet,
          color: 'indigo',
          path: '/settings/opening-balances'
        },
        {
          id: 'activity-recovery',
          title: 'Activity & Recovery',
          description: 'Audit deletes, reversals, undos. Restore where possible',
          icon: History,
          color: 'teal',
          path: '/activity-recovery'
        }
      ]
    },
    monitoring: {
      label: 'Quality & Automation',
      icon: Brain,
      description: 'AI accuracy and automation',
      features: []
    },
    finance: {
      label: 'Finance & Loans',
      icon: Briefcase,
      description: 'Manage loans and financial obligations',
      features: [
        {
          id: 'loans',
          title: 'Loan Schedule',
          description: 'Track loans, payments, and amortisation',
          icon: Briefcase,
          color: 'blue',
          path: '/loans'
        },
        {
          id: 'investments',
          title: 'Investment Portfolio',
          description: 'Track investments, returns, and performance',
          icon: TrendingUp,
          color: 'emerald',
          path: '/investments'
        },
        {
          id: 'special-items-tracking',
          title: 'Tracking & Untracked Items',
          description: 'Track untracked loan/investment postings, stop tracking, export schedules by GL',
          icon: Briefcase,
          color: 'amber',
          path: '/special-items/tracking'
        }
      ]
    }
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    teal: 'bg-teal-100 text-teal-700',
    slate: 'bg-slate-100 text-slate-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    orange: 'bg-orange-100 text-orange-700',
    green: 'bg-green-100 text-green-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    violet: 'bg-violet-100 text-violet-700',
    sky: 'bg-sky-100 text-sky-700',
    rose: 'bg-rose-100 text-rose-700',
    fuchsia: 'bg-fuchsia-100 text-fuchsia-700'
  };

  const FeatureCard = ({ feature }) => {
    // iter1106 - render locked state when the feature requires a higher
    // tier. `supportsFeature` short-circuits to true for features not in
    // FEATURE_MIN_TIER, so non-gated tiles are unaffected.
    const { supportsFeature, tier } = useBilling() || {};
    const isGated = !!feature.feature;
    const canAccess = isGated ? (supportsFeature ? supportsFeature(feature.feature) : true) : true;
    const requiredTier = feature.feature === 'payroll' ? 'Growth' : 'Higher';

    const handleClick = () => {
      if (isGated && !canAccess) {
        if (isIOSNative()) {
          // iOS App Store build: neutral copy, no pricing steering.
          toast.info(`${feature.title} isn't available on your current plan.`);
          return;
        }
        toast.info(`${feature.title} is on the ${requiredTier} plan. Upgrade to unlock.`);
        navigate('/pricing');
        return;
      }
      navigate(feature.path);
    };

    return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="cursor-pointer"
      onClick={handleClick}
      data-testid={`feature-card-${feature.id}`}
    >
      <Card className={`h-full hover:shadow-lg transition-all border-2 hover:border-[#283F8B]/30 ${isGated && !canAccess ? 'opacity-70' : ''}`}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className={`w-12 h-12 rounded-xl ${colorClasses[feature.color]} flex items-center justify-center`}>
              <feature.icon className="w-6 h-6" />
            </div>
            {feature.badge && (
              <Badge className="bg-gradient-to-r from-[#283F8B] to-[#108981] text-white">
                {feature.badge}
              </Badge>
            )}
            {isGated && !canAccess && (
              <Badge
                variant="outline"
                className="border-amber-400 text-amber-700 bg-amber-50"
                data-testid={`feature-lock-${feature.id}`}
              >
                {requiredTier}+
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
          <p className="text-sm text-muted-foreground mb-3">
            {feature.description}
            {isGated && !canAccess && (tier ? ` · Available on ${requiredTier} plan (you're on ${tier}).` : ` · Available on the ${requiredTier} plan.`)}
          </p>
          {(feature.stat || feature.substat) && (
            <div className="pt-3 border-t border-border">
              {feature.stat && (
                <p className="text-sm font-medium text-foreground">{feature.stat}</p>
              )}
              {feature.substat && (
                <p className="text-xs text-muted-foreground">{feature.substat}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#108981]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="advanced-view-hub">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#283F8B]/10 to-[#108981]/10 flex items-center justify-center">
              <Settings className="w-7 h-7 text-[#283F8B]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">More Tools</h1>
              <p className="text-muted-foreground">
                Books, operations, period close, tax centre and more: all in one place
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-[#108981] border-[#108981]">
            Pro Features
          </Badge>
        </div>

        {/* Section Tabs
            iter1078 - filter out empty sections so a section with no
            features doesn't render a dead tab. */}
        <Tabs value={activeSection} onValueChange={setActiveSection} className="space-y-6">
          <TabsList className="flex flex-wrap w-full max-w-4xl h-auto p-1 gap-1">
            {Object.entries(sections).filter(([, s]) => (s.features || []).length > 0).map(([key, section]) => (
              <TabsTrigger 
                key={key} 
                value={key} 
                className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-2 px-2 sm:px-4 data-[state=active]:bg-[#283F8B] data-[state=active]:text-white"
                data-testid={`section-tab-${key}`}
              >
                <section.icon className="w-4 h-4" />
                <span className="text-[11px] sm:text-sm leading-tight text-center sm:text-left">{section.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Section Contents */}
          {Object.entries(sections).filter(([, s]) => (s.features || []).length > 0).map(([key, section]) => (
            <TabsContent key={key} value={key} className="space-y-4" data-testid={`section-content-${key}`}>
              <div className="flex items-center gap-2 mb-4">
                <section.icon className="w-5 h-5 text-[#283F8B]" />
                <h2 className="text-xl font-semibold">{section.label}</h2>
                <span className="text-sm text-muted-foreground">· {section.description}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.features.map((feature) => (
                  <FeatureCard key={feature.id} feature={feature} />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Quick Access Footer */}
        <div className="mt-8 pt-6 border-t border-border">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Quick Access</h3>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/chart-of-accounts')}
              className="gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Chart of Accounts
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/manual-journals')}
              className="gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Manual Journals
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/fixed-assets')}
              className="gap-2"
            >
              <Briefcase className="w-4 h-4" />
              Fixed Assets
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/data-migration')}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              Import Data
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/fx-rates')}
              className="gap-2"
            >
              <DollarSign className="w-4 h-4" />
              FX Rates
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdvancedViewPage;
